// FROM: https://github.com/modusign/modusign-client/blob/1f2cc00e317b3cc6ddf42ace319c72036470a423/app/src/containers/RequesterFieldsBoard/index.tsx

import { faImage, faQuestionCircle, faSquareCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AlertModal, Box, Flex, Text, TextLink, Tooltip } from '@modusign/design-system';
import { useFields } from '@modusign/react-signing';
import { useUIState } from '@modusign/react-signing/setup';
import { FieldNodeType } from '@modusign/react-signing/types';
import { useMemo, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { useSelector } from 'react-redux';

import TextIcon from '@app/components/Icons/TextIcon';
import SetupButton from '@app/components/SetupButton';
import { Viewport } from '@app/features/apply-react-signing/components/FieldRenderer/types';
import { getPageInField } from '@app/features/apply-react-signing/libs/getFiledNodePage';
import { scaleSelector } from '@app/libs/reactPdfJs/store/selectors';

import './RequesterFieldsBoard.scss';

const defaultProps = {
  scrollTop: 0,
};

interface RequesterFieldsBoardProps {
  requesterId: string;
  viewports: Viewport[];
}

const IMAGE_MAX_COUNT = 5;

function RequesterFieldsBoard({ requesterId, viewports }: RequesterFieldsBoardProps) {
  const { ui, setInsertingField } = useUIState();
  const { fields } = useFields();
  const scale = useSelector(scaleSelector);

  const [isOpenTooltip, setIsOpenTooltip] = useState(false);

  const requesterFields = useMemo(
    () => fields.filter((field) => field.participantReference.type === 'REQUESTER'),
    [fields]
  );

  const requesterTextFieldCount = useMemo(
    () => requesterFields.filter((field) => field.type === 'TEXT').length ?? 0,
    [requesterFields]
  );

  const requesterCheckBoxFieldCount = useMemo(
    () => requesterFields.filter((field) => field.type === 'CHECKBOX').length ?? 0,
    [requesterFields]
  );

  const requesterImageFieldCount = useMemo(
    () => requesterFields.filter((field) => field.type === 'IMAGE').length ?? 0,
    [requesterFields]
  );

  const isInserting = !!ui.insertingField;
  const [isImageInputExceededModalOpen, setImageInputExceededModalOpen] = useState(false);

  function handleFieldCreationClicked(type: FieldNodeType) {
    if (isInserting) {
      return;
    }

    if (type === 'IMAGE' && IMAGE_MAX_COUNT <= requesterImageFieldCount) {
      setImageInputExceededModalOpen(true);
      return;
    }
    const lastModified = ui.lastInsertedField.SIGNER[type];
    const page = lastModified ? getPageInField(lastModified) : 1;

    setInsertingField({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      viewport: viewports.find((viewport) => viewport.pageNumber === page)!,
      participantId: requesterId,
      pdfScale: scale,
      type,
      page,
    });
  }

  return (
    <>
      <Box
        aria-label="requester fields board"
        backgroundColor="cardBackgroundNeutralBase"
        borderColor="dividerNeutralBase"
        borderRadius="6px"
        borderStyle="solid"
        borderWidth="1px"
        position="relative"
        px={4}
        py={6}
      >
        <Flex alignItems="center" justifyContent="space-between" mb={5} width="100%">
          <Text color="textNeutralBase" fontSize="md" fontWeight="medium">
            문서 편집
          </Text>
          <Tooltip
            content={
              <OutsideClickHandler onOutsideClick={() => setIsOpenTooltip(false)}>
                <Flex flexDirection="column">
                  <Text>
                    현재 문서에 필요한 내용을 추가할 수 있습니다.
                    <br />
                    요청 이후 수정이 불가능합니다.
                  </Text>
                  <TextLink
                    href="https://support.modusign.co.kr/hc/ko/articles/360022334274?utm_source=requesterfield"
                    mt={2}
                    rel="noopener noreferrer"
                    sx={{
                      '&:hover': {
                        color: 'textNeutralLightest',
                      },
                    }}
                    target="_blank"
                  >
                    자세히
                  </TextLink>
                </Flex>
              </OutsideClickHandler>
            }
            isOpen={isOpenTooltip}
            placement="right"
          >
            <Box
              color="iconNeutralDark"
              fontSize="15px"
              sx={{
                cursor: isOpenTooltip ? 'default' : 'pointer',
              }}
              onClick={() => setIsOpenTooltip(true)}
            >
              <FontAwesomeIcon icon={faQuestionCircle} />
            </Box>
          </Tooltip>
        </Flex>
        <Flex className="RequesterFieldsBoard__btn-container" flexDirection="column" gap="2px">
          <SetupButton
            count={requesterTextFieldCount}
            leftIcon={
              <Flex
                alignItems="center"
                className="RequesterFieldsBoard__btn-container__add-text-field-button"
                color="textNeutralLight"
                fontSize="19px"
                justifyContent="center"
              >
                <TextIcon />
              </Flex>
            }
            text="텍스트"
            onClick={() => handleFieldCreationClicked('TEXT')}
          />
          <SetupButton
            count={requesterCheckBoxFieldCount}
            leftIcon={
              <Flex
                alignItems="center"
                color="textNeutralLight"
                fontSize="19px"
                justifyContent="center"
              >
                <FontAwesomeIcon icon={faSquareCheck} />
              </Flex>
            }
            text="체크박스"
            onClick={() => handleFieldCreationClicked('CHECKBOX')}
          />
          <SetupButton
            count={requesterImageFieldCount}
            leftIcon={
              <Flex
                alignItems="center"
                color="textNeutralLight"
                fontSize="19px"
                justifyContent="center"
              >
                <FontAwesomeIcon icon={faImage} />
              </Flex>
            }
            text="이미지"
            onClick={() => handleFieldCreationClicked('IMAGE')}
          />
        </Flex>
      </Box>
      {isImageInputExceededModalOpen && (
        <AlertModal
          isOpen={isImageInputExceededModalOpen}
          title="이미지 입력 개수 초과"
          variant="basic"
          onClose={() => setImageInputExceededModalOpen(false)}
        >
          최대 5개까지 입력할 수 있습니다.
        </AlertModal>
      )}
    </>
  );
}

RequesterFieldsBoard.defaultProps = defaultProps;

export default RequesterFieldsBoard;
